/**
 * @zendir/ui - TelemetryCard Component
 *
 * Enterprise-grade spacecraft telemetry data card.
 *
 * AstroUXDS Compliance:
 * - Status colors used semantically (battery level, health status)
 * - Dual-coded status indicators (color + shape via StatusIndicator)
 * - Zendir accent colors for non-status elements (icons, decorative)
 * - Tabular numbers with fontVariantNumeric for data display
 * - Sentence-case labels per Tier 3 compliance
 *
 * Features:
 * - Full null-safety with graceful fallbacks
 * - WCAG 2.1 AA accessibility
 * - Subsystem toggles (power, attitude, comms)
 * - Anomaly alerts
 * - Loading and empty states
 */

import React, { memo, useMemo, useState } from "react";
import type { TelemetryData } from "../types";
import { useTheme } from "../theme";
import { StatusIndicator } from "../astro";
import { DataValue, DataValueGroup } from "../core/data/DataValue";
import { getPropertyConfig } from "../core/data/propertyConfig";
import { AstroIcon } from "../core/display/AstroIcon";
import { CardHeader } from "../core/display/CardHeader";
import { HeaderIconWithStatus } from "../core/display/HeaderIconWithStatus";
import { Icon, type IconName } from "../core/display/Icon";
import { useCompactMode } from "../hooks";
import { useCardBorderStyle } from "../context/DisplaySettingsContext";
import {
  safeNumber,
  formatTime,
  classNames,
  getStatusColor,
  deriveBatteryStatus,
  normalizeStatus,
  type StatusLevel,
} from "../utils";

export interface TelemetryCardProps {
  /** Telemetry data */
  telemetry?: TelemetryData;
  /** Spacecraft name */
  spacecraftName?: string;
  /** Show power subsystem */
  showPower?: boolean;
  /** Show attitude subsystem */
  showAttitude?: boolean;
  /** Show communications */
  showComms?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
  /** Enable compact mode - shows condensed view that expands on hover/click */
  compact?: boolean;
  /** Start expanded when in compact mode (pinned state) */
  defaultExpanded?: boolean;
  /** Callback when pin state changes in compact mode */
  onPinChange?: (isPinned: boolean) => void;
}

/**
 * Get health status level
 * Uses normalizeStatus utility to convert SDK terms to StatusLevel
 */
function getHealthStatus(healthStatus: string | undefined): StatusLevel {
  return normalizeStatus(healthStatus);
}

/**
 * Get battery status based on level
 * @deprecated Use deriveBatteryStatus from utils instead
 */
function getBatteryStatus(level: number | undefined): StatusLevel {
  return deriveBatteryStatus(level);
}

/**
 * TelemetryCard - Displays spacecraft telemetry data
 *
 * @example
 * ```tsx
 * <TelemetryCard
 *   telemetry={telemetryData}
 *   spacecraftName="ISS"
 *   showPower
 *   showAttitude
 *   showComms
 * />
 * ```
 */
export const TelemetryCard = memo(function TelemetryCard({
  telemetry,
  spacecraftName,
  showPower = true,
  showAttitude = true,
  showComms = true,
  loading = false,
  className = "",
  compact = false,
  defaultExpanded = false,
  onPinChange,
}: TelemetryCardProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const isBoldVariant = theme === "transparent-bold";
  const isMinimalVariant = theme === "transparent-minimal";
  const useGlassMode = isBoldVariant || isMinimalVariant;

  // Glass accent colors for bold/minimal themes (matching Container/AstroChart)
  const glassAccentColor = tokens.colors.accent.primary;
  const glassAccentMuted = `${glassAccentColor}66`;

  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};
  // Get card border style based on accentBorders setting
  const cardBorderStyle = useCardBorderStyle(tokens, isTransparentTheme);

  // Glass mode container style (matching Container/AstroChart for bold/minimal)
  const glassContainerStyle: React.CSSProperties = useGlassMode
    ? {
        backgroundColor: "rgba(10, 15, 25, 0.35)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${glassAccentMuted}`,
        borderTop: isBoldVariant
          ? `2px solid ${glassAccentColor}`
          : `1px solid ${glassAccentColor}`,
      }
    : {};

  // Compact mode state management
  const {
    expanded,
    isPinned,
    isHovered: _isHovered,
    handlers,
    transitionDuration: compactTransition,
  } = useCompactMode({
    compact,
    defaultExpanded,
    onPinChange,
  });

  // Track if we're animating to prevent flicker
  const [_isAnimating, _setIsAnimating] = useState(false);

  // Smooth animation timing
  const animationDuration = prefersReducedMotion ? 0 : 250;
  const animationEasing = "cubic-bezier(0.4, 0, 0.2, 1)";

  // Computed statuses
  const healthStatus = useMemo(
    () => getHealthStatus(telemetry?.health?.status),
    [telemetry?.health?.status],
  );

  const batteryStatus = useMemo(
    () => getBatteryStatus(telemetry?.power?.batteryLevel),
    [telemetry?.power?.batteryLevel],
  );

  const transitionDuration = prefersReducedMotion ? "0ms" : "200ms";

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames("zendir-telemetry-card", "loading", className)}
        role="article"
        aria-busy="true"
        aria-label="Loading telemetry data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          ...cardBorderStyle,
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.md,
          minHeight: 200,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              backgroundColor: "rgba(255,255,255,0.1)",
            }}
          />
          <div
            style={{
              height: 16,
              width: "40%",
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            <div
              style={{
                height: 12,
                width: "30%",
                backgroundColor: "rgba(255,255,255,0.08)",
                borderRadius: 2,
                marginBottom: 8,
              }}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 8,
              }}
            >
              {[1, 2, 3, 4].map((j) => (
                <div
                  key={j}
                  style={{
                    height: 32,
                    backgroundColor: "rgba(255,255,255,0.06)",
                    borderRadius: 4,
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!telemetry) {
    return (
      <div
        className={classNames("zendir-telemetry-card", "empty", className)}
        role="article"
        aria-label="No telemetry data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          ...(tokens.colors.border.cardStyleDashed ?? {}),
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.lg,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        <AstroIcon
          name="antenna"
          size="normal"
          label=""
          style={{ opacity: 0.5 }}
        />
        <p
          style={{
            margin: "8px 0 0 0",
            fontSize: tokens.typography.body[2].fontSize,
          }}
        >
          No telemetry data available
        </p>
      </div>
    );
  }

  const sectionStyle: React.CSSProperties = {
    marginBottom: tokens.spacing.md,
    paddingBottom: tokens.spacing.md,
    borderBottom: `1px solid ${tokens.colors.border.muted}`,
  };

  /** Fixed width so all section and metric icons align on one vertical line */
  const iconColumnWidth = 20;
  const iconGap = tokens.spacing.sm;
  const rowGap = tokens.spacing.smd;
  const sectionGridColumns = `${iconColumnWidth}px 1fr ${iconColumnWidth}px 1fr`;

  // Compact metric item component for consistent styling
  const CompactMetric = ({
    icon,
    label,
    value,
    color,
    astroIcon,
  }: {
    icon?: string;
    label: string;
    value: string | number;
    color?: string;
    astroIcon?: string;
  }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        padding: `2px 0`,
      }}
    >
      {astroIcon ? (
        <AstroIcon
          name={astroIcon}
          size={12}
          color={color ?? tokens.colors.text.secondary}
        />
      ) : icon ? (
        <Icon
          name={icon as IconName}
          size={12}
          color={color ?? tokens.colors.text.secondary}
        />
      ) : null}
      <span
        style={{
          fontSize: tokens.typography.fontSize.micro, // 0.5625rem / 9px (AstroUXDS micro)
          fontWeight: tokens.typography.fontWeight.medium,
          fontFamily: tokens.typography.fontFamily.primary, // Public Sans → Roboto fallback → system stack
          color: tokens.colors.text.tertiary,
          textTransform: "uppercase",
          minWidth: 8,
        }}
      >
        {label.charAt(0)}
      </span>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          fontFamily: tokens.typography.fontFamily.mono,
          fontSize: tokens.typography.fontSize.xs,
          color: color ?? tokens.colors.text.primary,
          fontWeight: tokens.typography.fontWeight.medium,
        }}
      >
        {value}
      </span>
    </div>
  );

  // Compact view content - reusable for both states
  const renderCompactContent = () => {
    const power = telemetry?.power ?? telemetry?.subsystems?.power;
    const attitude = telemetry?.attitude ?? telemetry?.subsystems?.attitude;
    const comms = telemetry?.communications ?? telemetry?.subsystems?.comms;
    const thermal = telemetry?.subsystems?.thermal;
    const compactStatusColor = getStatusColor(healthStatus);

    return (
      <>
        {/* Header: Icon with status badge overlay → Name → (flex) → Badges on right */}
        {/* Follows MonitoringIcon layout pattern for consistency */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: tokens.spacing.xs,
            paddingBottom: tokens.spacing.xs,
            borderBottom: useGlassMode
              ? "none"
              : `1px solid ${tokens.colors.border.muted}`,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Left side: Icon with status badge + Name */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              minWidth: 0,
              flex: 1,
            }}
          >
            {/* Icon with status badge - MonitoringIcon pattern */}
            <HeaderIconWithStatus
              icon="antenna-receive"
              size={20}
              status={healthStatus}
              statusColor={compactStatusColor}
            />
            {/* Name - accent color for glass mode */}
            <span
              style={{
                fontWeight: tokens.typography.fontWeight.semibold,
                fontSize: tokens.typography.fontSize.sm,
                fontFamily: tokens.typography.fontFamily.primary,
                color: useGlassMode ? glassAccentColor : undefined,
                textShadow: useGlassMode
                  ? `0 0 16px ${glassAccentMuted}`
                  : undefined,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {spacecraftName ? spacecraftName.split(" ")[0] : "Telemetry"}
            </span>
            {/* Accent line for glass mode */}
            {useGlassMode && (
              <div
                style={{
                  flex: 1,
                  height: isBoldVariant ? "2px" : "1px",
                  minWidth: 20,
                  background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 60%, transparent 100%)`,
                  boxShadow: isBoldVariant
                    ? `0 0 8px ${glassAccentMuted}`
                    : undefined,
                }}
              />
            )}
          </div>

          {/* Right side: Badges (pin indicator, etc.) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.xs,
              flexShrink: 0,
            }}
          >
            {isPinned && (
              <Icon
                name="lock"
                size={10}
                color={tokens.colors.accent.primary}
              />
            )}
          </div>
        </div>

        {/* Metrics grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
          }}
        >
          {/* Power metrics */}
          {showPower && power && (
            <>
              {power.batteryLevel !== undefined && (
                <CompactMetric
                  icon="battery"
                  label="Battery"
                  value={`${safeNumber(power.batteryLevel, 0)}%`}
                  color={getStatusColor(batteryStatus)}
                />
              )}
              {(power.solarGeneration ?? power.solarArrayOutput) !==
                undefined && (
                <CompactMetric
                  astroIcon="solar"
                  label="Solar"
                  value={`${safeNumber(power.solarGeneration ?? power.solarArrayOutput, 1)}W`}
                  color={tokens.colors.status.standby}
                />
              )}
              {power.voltage !== undefined && (
                <CompactMetric
                  astroIcon="propulsion-power"
                  label="Voltage"
                  value={`${safeNumber(power.voltage, 1)}V`}
                />
              )}
            </>
          )}

          {/* Attitude metrics */}
          {showAttitude && attitude && (
            <>
              {attitude.pointingError !== undefined && (
                <CompactMetric
                  astroIcon="explore"
                  label="Pointing"
                  value={`${safeNumber(attitude.pointingError, 2)}°`}
                />
              )}
              {attitude.targetMode && (
                <CompactMetric
                  astroIcon="explore"
                  label="Mode"
                  value={attitude.targetMode}
                />
              )}
            </>
          )}

          {/* Thermal metrics */}
          {thermal && (
            <>
              {thermal.cpuTemp !== undefined && (
                <CompactMetric
                  icon="thermometer"
                  label="CPU"
                  value={`${safeNumber(thermal.cpuTemp, 0)}°C`}
                  color={
                    thermal.cpuTemp > 50
                      ? tokens.colors.status.caution
                      : undefined
                  }
                />
              )}
            </>
          )}

          {/* Comms metrics */}
          {showComms && comms && (
            <>
              {comms.signalStrength !== undefined && (
                <CompactMetric
                  astroIcon="antenna"
                  label="Signal"
                  value={`${safeNumber(comms.signalStrength, 0)}%`}
                />
              )}
              {(comms.downlinkRate ?? comms.dataRate) !== undefined && (
                <CompactMetric
                  astroIcon="antenna-transmit"
                  label="Rate"
                  value={`${safeNumber(comms.downlinkRate ?? comms.dataRate, 0)}Mb`}
                />
              )}
              {comms.transmitterStatus && (
                <CompactMetric
                  astroIcon="antenna-receive"
                  label="TX"
                  value={
                    comms.transmitterStatus === "transmitting" ? "ON" : "OFF"
                  }
                  color={
                    comms.transmitterStatus === "transmitting"
                      ? tokens.colors.status.normal
                      : tokens.colors.status.off
                  }
                />
              )}
            </>
          )}
        </div>
      </>
    );
  };

  // Compact mode with overlay expansion - prevents layout shift
  if (compact) {
    return (
      <article
        className={classNames(
          "zendir-telemetry-card",
          "compact",
          expanded ? "expanded" : "",
          className,
        )}
        {...handlers}
        aria-label={
          spacecraftName
            ? `${spacecraftName} Telemetry${expanded ? "" : " (compact)"}`
            : "Telemetry Data"
        }
        style={{
          position: "relative",
          overflow: "hidden",
          // Use glass container style for bold/minimal themes
          ...(useGlassMode
            ? glassContainerStyle
            : {
                backgroundColor: cardBg,
                ...cardGlass,
                ...cardBorderStyle,
              }),
          borderRadius: tokens.borderRadius.lg,
          padding: expanded ? tokens.spacing.md : tokens.spacing.sm,
          fontFamily: tokens.typography.fontFamily.primary,
          color: tokens.colors.text.primary,
          fontSize: expanded
            ? tokens.typography.fontSize.base
            : tokens.typography.fontSize.sm,
          cursor: "pointer",
          transition: `all ${animationDuration}ms ${animationEasing}`,
          boxShadow: expanded
            ? `0 12px 40px rgba(0,0,0,0.4), 0 0 0 1px ${tokens.colors.accent.primary}30`
            : "none",
          zIndex: expanded ? 50 : 1,
        }}
      >
        {/* Glass gradient overlay for bold/minimal themes */}
        {useGlassMode && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}
        {expanded
          ? // Full expanded content - resolve data with fallbacks like compact view
            (() => {
              const power = telemetry?.power ?? telemetry?.subsystems?.power;
              const attitude =
                telemetry?.attitude ?? telemetry?.subsystems?.attitude;
              const comms =
                telemetry?.communications ?? telemetry?.subsystems?.comms;
              return (
                <>
                  {/* Header - consistent icon/status with compact view */}
                  <header
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: tokens.spacing.md,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: tokens.spacing.sm,
                      }}
                    >
                      {/* Icon with status badge - MonitoringIcon pattern */}
                      <HeaderIconWithStatus
                        icon="antenna-receive"
                        size={24}
                        status={healthStatus}
                      />
                      <h3
                        style={{
                          margin: 0,
                          fontSize: tokens.typography.fontSize.md,
                          fontWeight: tokens.typography.fontWeight.semibold,
                        }}
                      >
                        {spacecraftName
                          ? `${spacecraftName} Telemetry`
                          : "Telemetry"}
                      </h3>
                      {isPinned && (
                        <Icon
                          name="lock"
                          size={12}
                          color={tokens.colors.accent.primary}
                          style={{ marginLeft: 4 }}
                        />
                      )}
                    </div>
                    <StatusIndicator
                      status={healthStatus}
                      label={healthStatus}
                      small
                    />
                  </header>

                  {/* Power Section - fixed icon column so all icons align vertically */}
                  {showPower && (
                    <section style={sectionStyle} aria-label="Power subsystem">
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: sectionGridColumns,
                          gap: `${rowGap} ${iconGap}`,
                          alignItems: "center",
                        }}
                      >
                        <AstroIcon
                          name="propulsion-power"
                          size={12}
                          color={tokens.colors.text.tertiary}
                        />
                        <h4
                          style={{
                            margin: 0,
                            gridColumn: "2 / -1",
                            fontSize: tokens.typography.fontSize.xs,
                            fontWeight: tokens.typography.fontWeight.medium,
                            color: tokens.colors.text.secondary,
                          }}
                        >
                          Power
                        </h4>
                        <AstroIcon
                          name={getPropertyConfig("battery").icon}
                          size={16}
                          color={getStatusColor(batteryStatus)}
                        />
                        <DataValue
                          property="battery"
                          value={power?.batteryLevel}
                          size="sm"
                          variant="card"
                          showIcon={false}
                        />
                        <AstroIcon
                          name={getPropertyConfig("voltage").icon}
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          property="voltage"
                          value={power?.voltage}
                          size="sm"
                          variant="card"
                          showIcon={false}
                        />
                        <AstroIcon
                          name={getPropertyConfig("solarPower").icon}
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          property="solarPower"
                          value={
                            power?.solarGeneration ?? power?.solarArrayOutput
                          }
                          size="sm"
                          variant="card"
                          status="standby"
                          showIcon={false}
                        />
                        <AstroIcon
                          name={getPropertyConfig("load").icon}
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          property="load"
                          value={power?.consumption}
                          size="sm"
                          variant="card"
                          showIcon={false}
                        />
                      </div>

                      {/* Battery progress bar */}
                      <div
                        style={{
                          marginTop: tokens.spacing.sm,
                          height: 4,
                          backgroundColor: tokens.colors.border.muted,
                          borderRadius: 2,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          role="progressbar"
                          aria-valuenow={power?.batteryLevel ?? 0}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label="Battery level"
                          style={{
                            height: "100%",
                            width: `${Math.min(100, Math.max(0, power?.batteryLevel ?? 0))}%`,
                            backgroundColor: getStatusColor(batteryStatus),
                            borderRadius: 2,
                            transition: `width ${transitionDuration} ease-out`,
                          }}
                        />
                      </div>
                    </section>
                  )}

                  {/* Attitude Section - fixed icon column so all icons align vertically */}
                  {showAttitude && (
                    <section
                      style={sectionStyle}
                      aria-label="Attitude subsystem"
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: sectionGridColumns,
                          gap: `${rowGap} ${iconGap}`,
                          alignItems: "center",
                        }}
                      >
                        <AstroIcon
                          name="explore"
                          size={12}
                          color={tokens.colors.text.tertiary}
                        />
                        <h4
                          style={{
                            margin: 0,
                            gridColumn: "2 / -1",
                            fontSize: tokens.typography.fontSize.xs,
                            fontWeight: tokens.typography.fontWeight.medium,
                            color: tokens.colors.text.secondary,
                          }}
                        >
                          Attitude
                        </h4>
                        <AstroIcon
                          name={getPropertyConfig("roll").icon}
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          property="roll"
                          value={
                            attitude?.eulerAngles?.roll ??
                            attitude?.pointingError
                          }
                          size="sm"
                          variant="card"
                          showStatus={false}
                          showIcon={false}
                        />
                        <AstroIcon
                          name={getPropertyConfig("pitch").icon}
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          property="pitch"
                          value={attitude?.eulerAngles?.pitch}
                          size="sm"
                          variant="card"
                          showStatus={false}
                          showIcon={false}
                        />
                        <AstroIcon
                          name={getPropertyConfig("yaw").icon}
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          property="yaw"
                          value={attitude?.eulerAngles?.yaw}
                          size="sm"
                          variant="card"
                          showStatus={false}
                          showIcon={false}
                        />
                        <AstroIcon
                          name="explore"
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          label="Mode"
                          icon="explore"
                          value={attitude?.targetMode ?? "--"}
                          size="sm"
                          variant="card"
                          showStatus={false}
                          showIcon={false}
                        />
                      </div>
                    </section>
                  )}

                  {/* Communications Section - fixed icon column so all icons align vertically */}
                  {showComms && (
                    <section
                      style={{ marginBottom: tokens.spacing.sm }}
                      aria-label="Communications subsystem"
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: sectionGridColumns,
                          gap: `${rowGap} ${iconGap}`,
                          alignItems: "center",
                        }}
                      >
                        <AstroIcon
                          name="antenna"
                          size={12}
                          color={tokens.colors.text.tertiary}
                        />
                        <h4
                          style={{
                            margin: 0,
                            gridColumn: "2 / -1",
                            fontSize: tokens.typography.fontSize.xs,
                            fontWeight: tokens.typography.fontWeight.medium,
                            color: tokens.colors.text.secondary,
                          }}
                        >
                          Communications
                        </h4>
                        <AstroIcon
                          name="antenna-transmit"
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          label="Status"
                          icon="antenna-transmit"
                          value={
                            comms?.transmitterStatus ?? comms?.status ?? "--"
                          }
                          size="sm"
                          variant="card"
                          showStatus={false}
                          showIcon={false}
                        />
                        <AstroIcon
                          name={getPropertyConfig("signalStrength").icon}
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          property="signalStrength"
                          value={comms?.signalStrength}
                          size="sm"
                          variant="card"
                          showStatus={false}
                          showIcon={false}
                        />
                        <AstroIcon
                          name="antenna-receive"
                          size={16}
                          color={tokens.colors.text.tertiary}
                        />
                        <DataValue
                          label="Transmitted"
                          icon="antenna-receive"
                          value={
                            comms?.packetsTransmitted ?? comms?.dataRate ?? "--"
                          }
                          size="sm"
                          variant="card"
                          showStatus={false}
                          showIcon={false}
                        />
                        <span style={{ gridColumn: "4 / -1" }} />
                      </div>
                    </section>
                  )}

                  {/* Timestamp */}
                  {telemetry?.timestamp && (
                    <time
                      dateTime={telemetry.timestamp}
                      style={{
                        display: "block",
                        fontSize: tokens.typography.fontSize.xs,
                        color: tokens.colors.text.tertiary,
                        marginTop: tokens.spacing.sm,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatTime(telemetry.timestamp)}
                    </time>
                  )}
                </>
              );
            })()
          : // Compact content
            renderCompactContent()}
      </article>
    );
  }

  return (
    <article
      className={classNames(
        "zendir-telemetry-card",
        compact ? "expanded" : "",
        className,
      )}
      {...(compact ? handlers : {})}
      role={compact ? handlers.role : "article"}
      tabIndex={compact ? handlers.tabIndex : undefined}
      aria-expanded={compact ? handlers["aria-expanded"] : undefined}
      aria-label={
        spacecraftName ? `${spacecraftName} Telemetry` : "Telemetry Data"
      }
      style={{
        position: "relative",
        overflow: "hidden",
        ...(useGlassMode
          ? glassContainerStyle
          : {
              backgroundColor: cardBg,
              ...cardGlass,
              ...cardBorderStyle,
            }),
        borderRadius: tokens.borderRadius.lg,
        padding: tokens.spacing.md,
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
        fontSize: tokens.typography.fontSize.sm,
        cursor: compact ? "pointer" : undefined,
        transition: compact ? `all ${compactTransition} ease-out` : undefined,
      }}
    >
      {/* Glass gradient overlay for bold/minimal themes */}
      {useGlassMode && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Unified Card Header - handles all theme variants */}
      <CardHeader
        icon="antenna-receive"
        title={spacecraftName ? `${spacecraftName} Telemetry` : "Telemetry"}
        status={healthStatus}
        isPinned={compact && isPinned}
      />

      {/* Power Section - fixed icon column so all icons align vertically */}
      {showPower && (
        <section style={sectionStyle} aria-label="Power subsystem">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: sectionGridColumns,
              gap: `${rowGap} ${iconGap}`,
              alignItems: "center",
            }}
          >
            <AstroIcon
              name="propulsion-power"
              size={12}
              color={tokens.colors.text.tertiary}
            />
            <h4
              style={{
                margin: 0,
                gridColumn: "2 / -1",
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                color: tokens.colors.text.secondary,
              }}
            >
              Power
            </h4>
            <AstroIcon
              name={getPropertyConfig("battery").icon}
              size={16}
              color={getStatusColor(batteryStatus)}
            />
            <DataValue
              property="battery"
              value={telemetry.power?.batteryLevel}
              size="sm"
              variant="card"
              showIcon={false}
            />
            <AstroIcon
              name={getPropertyConfig("voltage").icon}
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              property="voltage"
              value={telemetry.power?.voltage}
              size="sm"
              variant="card"
              showIcon={false}
            />
            <AstroIcon
              name={getPropertyConfig("solarPower").icon}
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              property="solarPower"
              value={telemetry.power?.solarGeneration}
              size="sm"
              variant="card"
              status="standby"
              showIcon={false}
            />
            <AstroIcon
              name={getPropertyConfig("load").icon}
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              property="load"
              value={telemetry.power?.consumption}
              size="sm"
              variant="card"
              showIcon={false}
            />
          </div>

          {/* Battery progress bar */}
          <div
            style={{
              marginTop: tokens.spacing.sm,
              height: 4,
              backgroundColor: tokens.colors.border.muted,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <div
              role="progressbar"
              aria-valuenow={telemetry.power?.batteryLevel ?? 0}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Battery level"
              style={{
                height: "100%",
                width: `${Math.min(100, Math.max(0, telemetry.power?.batteryLevel ?? 0))}%`,
                backgroundColor: getStatusColor(batteryStatus),
                borderRadius: 2,
                transition: `width ${transitionDuration} ease-out`,
              }}
            />
          </div>
        </section>
      )}

      {/* Attitude Section - fixed icon column so all icons align vertically */}
      {showAttitude && (
        <section style={sectionStyle} aria-label="Attitude subsystem">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: sectionGridColumns,
              gap: `${rowGap} ${iconGap}`,
              alignItems: "center",
            }}
          >
            <AstroIcon
              name="explore"
              size={12}
              color={tokens.colors.text.tertiary}
            />
            <h4
              style={{
                margin: 0,
                gridColumn: "2 / -1",
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                color: tokens.colors.text.secondary,
              }}
            >
              Attitude
            </h4>
            <AstroIcon
              name={getPropertyConfig("roll").icon}
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              property="roll"
              value={telemetry.attitude?.eulerAngles?.roll}
              size="sm"
              variant="card"
              showStatus={false}
              showIcon={false}
            />
            <AstroIcon
              name={getPropertyConfig("pitch").icon}
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              property="pitch"
              value={telemetry.attitude?.eulerAngles?.pitch}
              size="sm"
              variant="card"
              showStatus={false}
              showIcon={false}
            />
            <AstroIcon
              name={getPropertyConfig("yaw").icon}
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              property="yaw"
              value={telemetry.attitude?.eulerAngles?.yaw}
              size="sm"
              variant="card"
              showStatus={false}
              showIcon={false}
            />
            <AstroIcon
              name="explore"
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              label="Mode"
              icon="explore"
              value={telemetry.attitude?.targetMode ?? "--"}
              size="sm"
              variant="card"
              showStatus={false}
              showIcon={false}
            />
          </div>
          {telemetry.attitude?.pointingError !== undefined && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: iconGap,
                marginTop: tokens.spacing.sm,
              }}
            >
              <AstroIcon
                name={getPropertyConfig("pointingError").icon}
                size={16}
                color={tokens.colors.text.tertiary}
              />
              <DataValue
                property="pointingError"
                value={telemetry.attitude.pointingError}
                size="sm"
                variant="inline"
                showIcon={false}
              />
            </div>
          )}
        </section>
      )}

      {/* Communications Section - fixed icon column so all icons align vertically */}
      {showComms && (
        <section
          style={{ marginBottom: tokens.spacing.md }}
          aria-label="Communications subsystem"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: sectionGridColumns,
              gap: `${rowGap} ${iconGap}`,
              alignItems: "center",
            }}
          >
            <AstroIcon
              name="antenna"
              size={12}
              color={tokens.colors.text.tertiary}
            />
            <h4
              style={{
                margin: 0,
                gridColumn: "2 / -1",
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                color: tokens.colors.text.secondary,
              }}
            >
              Communications
            </h4>
            <AstroIcon
              name="antenna-transmit"
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              label="Status"
              icon="antenna-transmit"
              value={telemetry.communications?.transmitterStatus ?? "--"}
              size="sm"
              variant="card"
              showIcon={false}
              status={
                telemetry.communications?.transmitterStatus === "transmitting"
                  ? "normal"
                  : telemetry.communications?.transmitterStatus === "error"
                    ? "critical"
                    : "off"
              }
            />
            <AstroIcon
              name={getPropertyConfig("queue").icon}
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              property="queue"
              value={telemetry.communications?.packetsQueued}
              size="sm"
              variant="card"
              showIcon={false}
            />
            <AstroIcon
              name="antenna-receive"
              size={16}
              color={tokens.colors.text.tertiary}
            />
            <DataValue
              label="Transmitted"
              icon="antenna-receive"
              value={safeNumber(
                (telemetry.communications?.bytesTransmitted ?? 0) / 1024,
                1,
              )}
              unit="KB"
              size="sm"
              variant="card"
              showStatus={false}
              showIcon={false}
            />
            <span style={{ gridColumn: "4 / -1" }} />
          </div>
        </section>
      )}

      {/* Anomalies Alert */}
      {telemetry.health?.anomalies && telemetry.health.anomalies.length > 0 && (
        <div
          role="alert"
          style={{
            backgroundColor: `${tokens.colors.status.critical}15`,
            border: `1px solid ${tokens.colors.status.critical}40`,
            borderRadius: tokens.borderRadius.md,
            padding: tokens.spacing.sm,
            marginTop: tokens.spacing.md,
          }}
        >
          <h4
            style={{
              margin: `0 0 ${tokens.spacing.xs} 0`,
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: tokens.typography.fontWeight.medium,
              color: tokens.colors.status.critical,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            ⚠️ Anomalies ({telemetry.health.anomalies.length})
          </h4>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {telemetry.health.anomalies.map(
              (
                anomaly: { id: string; message: string; severity?: string },
                idx: number,
              ) => (
                <li
                  key={idx}
                  style={{
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.status.critical,
                    marginTop: 2,
                  }}
                >
                  {typeof anomaly === "object" &&
                  anomaly !== null &&
                  "message" in anomaly
                    ? anomaly.message
                    : String(anomaly)}
                </li>
              ),
            )}
          </ul>
        </div>
      )}

      {/* Timestamp Footer */}
      <footer
        style={{
          marginTop: tokens.spacing.md,
          fontSize: tokens.typography.fontSize.xs,
          color: tokens.colors.text.secondary, // WCAG AA compliant - 4.5:1 contrast
          textAlign: "right",
        }}
      >
        <time dateTime={telemetry.timestamp}>
          {formatTime(telemetry.timestamp)} UTC
        </time>
      </footer>
    </article>
  );
});

// Note: DataCell is deprecated in favor of DataValue from core.
// Use <DataValue property="..." value={...} /> for automatic icon/status integration.
